import { Alert, Button } from "@mui/material";
import { Play20Px, ProgressButton } from "@qubit/autoparts";

import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";

import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import {
  useCloseWorkstationMutation,
  useStartBinReconfigurationMutation
} from "~/redux/warehouse/workstation.hooks";

export const StartBinReconfigurationButton = () => {
  const { t } = useTranslation();
  const navigate = useFcNavigate();
  const [startBinReconfiguration, { error: startBinReconfigurationError }] =
    useStartBinReconfigurationMutation();
  const [closeWorkstation, { error: closeWorkstationError }] =
    useCloseWorkstationMutation();
  const workstation = useAppSelector(selectThisWorkstation);
  const sourceBinConfigurationId = useAppSelector(
    (store) => store.binReconfiguration.sourceBinConfigurationId
  );
  const targetBinConfigurationId = useAppSelector(
    (store) => store.binReconfiguration.targetBinConfigurationId
  );
  const trackedPageName = useMixpanelPageName();

  const handleStartBinReconfiguration = async (isRetry = false) => {
    if (!workstation || !sourceBinConfigurationId || !targetBinConfigurationId)
      return;

    mixpanelTrack({
      trackedPageName,
      label: isRetry ? "Retry Start Reconfiguration" : "Start Reconfiguration",
      type: "Button Click",
      eventProperties: {
        "Source Bin Configuration Id": sourceBinConfigurationId,
        "Target Bin Configuration Id": targetBinConfigurationId,
        "Grid Name": workstation.autostoreGridName
      }
    });
    try {
      await closeWorkstation({
        autostoreGridId: workstation.autostoreGridId,
        workstationId: workstation.id
      }).unwrap();
      await startBinReconfiguration({
        workstationId: workstation.id,
        binConfigurationId: sourceBinConfigurationId
      }).unwrap();

      navigate({
        pathname: "/bin-reconfiguration",
        search: new URLSearchParams({
          sourceBinConfigurationId,
          targetBinConfigurationId,
          autostore: "true"
        }).toString()
      });
    } catch {
      // error handled by alert
    }
  };

  if (startBinReconfigurationError ?? closeWorkstationError) {
    return (
      <Alert
        variant="outlined"
        severity="error"
        action={
          <Button onClick={() => void handleStartBinReconfiguration(true)}>
            Retry
          </Button>
        }
      >
        {getMessageFromRtkError(
          startBinReconfigurationError ?? closeWorkstationError
        )}
      </Alert>
    );
  }

  return (
    <>
      <ProgressButton
        style={{ justifySelf: "end" }}
        size="large"
        startIcon={<Play20Px style={{ fill: "white" }} />}
        disabled={!sourceBinConfigurationId || !targetBinConfigurationId}
        onClick={() => void handleStartBinReconfiguration()}
      >
        {t("start reconfiguration")}
      </ProgressButton>
    </>
  );
};
